import {
  Box,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  useTheme,
} from '@chakra-ui/react';
import {
  PageHeader,
  MetaTags,
  PageToolbar,
  TeamMenu,
  DateFilter,
  eliminateHtmlTags,
  useDebounce,
  CustomSelect,
  ZeroStatePage,
} from '@devd-client/devd/components';
import { FaCodeBranch } from 'react-icons/fa';
import { FC, useEffect, useMemo, useState } from 'react';
import { AuthActionTypes, getUser, getOrgId } from '@devd-client/devd/auth';
import { AcceptanceByLanguage } from '../components/Copilot/AcceptanceByLanguage';
import { CopilotActiveUsers } from '../components/Copilot/CopilotActiveUsers';
import { CopilotLinesAccepted } from '../components/Copilot/CopilotLinesAccepted';
import { CopilotChatAcceptance } from '../components/Copilot/CopilotChatAcceptance';
import { useCopilotDashboard } from '../hooks/useCopilotDashboard';
import { useCopilotAiAdoptionPage } from '../api';

export const Copilot: FC = () => {
  const { selectedTeam, setSelectedTeam, appState, dispatch } =
    useCopilotDashboard();
  const { colors } = useTheme();
  const [branches, setBranches] = useState('');
  const [aiEditorFilter, setAiEditorFilter] = useState({
    label: 'All',
    value: '',
  });
  const aiEditorFilterOptions = useMemo(() => {
    return [
      {
        label: 'All',
        value: '',
      },
      {
        label: 'Github Copilot',
        value: 'github',
      },
      {
        label: 'Cursor',
        value: 'cursor',
      },
      {
        label: 'Claude Code',
        value: 'claude_code',
      },
      {
        label: 'Windsurf',
        value: 'windsurf',
      },
    ];
  }, []);

  const [searchTerm, setSearchTerm] = useState<string>(branches);
  const debouncedSearchTerm = useDebounce(searchTerm);
  const { data } = useCopilotAiAdoptionPage();

  useEffect(() => {
    setBranches(debouncedSearchTerm);
  }, [debouncedSearchTerm]);

  return (
    <>
      <MetaTags title="AI Adoption | Dashboard | Devdynamics" />

      {data && data?.dto?.enabled ? (
        <Box display={'flex'} flexDirection={'column'} width={'100%'}>
          <Box>
            <PageHeader
              headingText={'AI Adoption'}
              mb={'10px'}
              tooltip="Visualizes your team's AI code assistant usage metrics and effectiveness. Track acceptance rates across programming languages, monitor total suggestions and acceptances, and identify which languages benefit most from AI assistance. Use this data to optimize your development processes and understand how AI tools are enhancing your team's productivity."
            />
            <PageToolbar
              leftComp={
                <Flex align="flex-end" gap={3}>
                  <TeamMenu
                    selectedTeam={selectedTeam}
                    handleSelectedTeam={setSelectedTeam}
                    AuthActionTypes={AuthActionTypes}
                    getUser={getUser}
                    appState={appState}
                    dispatch={dispatch}
                  />
                  <CustomSelect
                    size="sm"
                    onChange={(selected: string) =>
                      setAiEditorFilter(
                        aiEditorFilterOptions.filter(
                          (option) => option.value === selected
                        )[0]
                      )
                    }
                    options={aiEditorFilterOptions}
                    defaultItem={aiEditorFilter}
                    label="AI Code Editor"
                  />
                  {(getOrgId() === 'YYUHAYDAT' || getOrgId() === 'OIDPPE') && (
                    <Flex gap={1} direction="column">
                      <Text
                        fontSize="xxs"
                        fontWeight="semibold"
                        color="text.secondary"
                      >
                        Applied Only for Avg. Contributors and Avg. Lines Added
                        Metrics
                      </Text>

                      <InputGroup size={'sm'}>
                        <InputLeftElement pointerEvents="none">
                          <FaCodeBranch color={colors.text.secondary} />
                        </InputLeftElement>
                        <Input
                          id="input"
                          name="input"
                          size={'sm'}
                          placeholder={'Contributed branch'}
                          value={searchTerm}
                          onChange={(e) => {
                            setSearchTerm(eliminateHtmlTags(e.target.value));
                          }}
                        />
                      </InputGroup>
                    </Flex>
                  )}
                </Flex>
              }
              rightComp={
                <DateFilter
                  getUser={getUser}
                  appState={appState}
                  dispatch={dispatch}
                  AuthActionTypes={AuthActionTypes}
                />
              }
            />
            <AcceptanceByLanguage
              branches={branches}
              type={aiEditorFilter.value}
            />
            <CopilotActiveUsers
              branches={branches}
              type={aiEditorFilter.value}
            />
            <CopilotLinesAccepted
              branches={branches}
              type={aiEditorFilter.value}
            />
            <CopilotChatAcceptance
              branches={branches}
              type={aiEditorFilter.value}
            />
          </Box>
        </Box>
      ) : (
        <ZeroStatePage
          data={data?.dto}
          featureAlert={data?.featureEnable === false ? true : false}
        />
      )}
    </>
  );
};

export default Copilot;
